import { useState } from "react";
import { Modal } from "react-bootstrap";
import { EngagementAction } from "@models/engagementAction";
import { MyTodosService } from "@services/myTodos/MyTodosService";
import { computeActionCtaState } from "./actionCtaState";
import showAlert from "src/shared/utils/generic";

interface ActionCtasProps {
  action: EngagementAction | null;
  userRoles: string[];
  onCompleted: () => void;
  onRebrandEdit?: () => void;
  /** Runs before the approve/request-changes mutation. Use to flush a pending comment. */
  beforeMutation?: () => Promise<void>;
  /**
   * True when a rejection would carry no explanation at all (MPN-1626). Request changes
   * then opens a blocking modal instead of submitting.
   *
   * Only Request changes is gated. Approve is deliberately left alone: an approval with
   * no comment says everything it needs to, and the ticket says so explicitly.
   */
  requestChangesNeedsComment?: boolean;
}

export default function ActionCtas({
  action, userRoles, onCompleted, onRebrandEdit, beforeMutation,
  requestChangesNeedsComment = false,
}: ActionCtasProps) {
  const state = computeActionCtaState(action, userRoles);
  const [submitting, setSubmitting] = useState(false);
  const [commentRequired, setCommentRequired] = useState(false);

  if (state.kind === "none" || !action) {
    return null;
  }

  if (state.kind === "review-info") {
    return null;
  }

  if (state.kind === "rebrand-info") {
    return (
      <div className="mt-action-ctas">
        <div className="mt-action-ctas__banner">
          Updates needed — edit in Ad Editor
          {onRebrandEdit && (
            <button
              style={{ marginLeft: 8, background: "transparent", border: "none", color: "#1e40af", cursor: "pointer" }}
              onClick={onRebrandEdit}
            >
              Open
            </button>
          )}
        </div>
      </div>
    );
  }

  const approve = async () => {
    setSubmitting(true);
    try {
      if (beforeMutation) await beforeMutation();
      await new MyTodosService().approve(action.engagement_id, action.id);
      showAlert("Approved", "success", 3000);
      onCompleted();
    } catch (e: any) {
      showAlert(e?.message ?? "Failed to approve", "error", 4000);
    } finally {
      setSubmitting(false);
    }
  };

  const requestChanges = async () => {
    // Checked here rather than by disabling the button, so the client is told why
    // (MPN-1626). A greyed-out Request changes with no explanation is the same dead end
    // from the other side.
    if (requestChangesNeedsComment) {
      setCommentRequired(true);
      return;
    }
    setSubmitting(true);
    try {
      if (beforeMutation) await beforeMutation();
      await new MyTodosService().requestChanges(action.engagement_id, action.id);
      showAlert("Changes requested", "success", 3000);
      onCompleted();
    } catch (e: any) {
      showAlert(e?.message ?? "Failed to request changes", "error", 4000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-action-ctas">
      {/* Blocking: the only way out is OK, and the rejection was never sent. */}
      <Modal show={commentRequired} onHide={() => setCommentRequired(false)} centered>
        <Modal.Body>
          Please provide a comment describing the requested changes.
        </Modal.Body>
        <Modal.Footer>
          <button
            className="mt-action-ctas__button mt-action-ctas__button--approve"
            onClick={() => setCommentRequired(false)}
          >
            OK
          </button>
        </Modal.Footer>
      </Modal>
      <button
        className="mt-action-ctas__button mt-action-ctas__button--approve"
        disabled={submitting}
        onClick={approve}
      >
        ✓ Approve
      </button>
      <button
        className="mt-action-ctas__button mt-action-ctas__button--reject"
        disabled={submitting}
        onClick={requestChanges}
      >
        Request changes
      </button>
    </div>
  );
}
